/**
 * Feature Flag Context
 * POV-0002: Frontend flag gating for drawer vs legacy view
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { featureFlagService } from '../services/featureFlagService';
import { useAuth } from './AuthContext';

const FeatureFlagContext = createContext(null);

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

/**
 * Hook to check a specific feature flag
 */
export const useFeatureFlag = (flagKey) => {
  const { flags, isLoading } = useFeatureFlags();
  return {
    enabled: flags[flagKey] || false,
    isLoading,
  };
};

export const FeatureFlagProvider = ({ children }) => {
  const [flags, setFlags] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated } = useAuth();

  // Load flags on mount and when auth state changes
  useEffect(() => {
    const loadFlags = async () => {
      setIsLoading(true);
      try {
        const loadedFlags = await featureFlagService.getAllFlags(true);
        setFlags(loadedFlags);
      } catch (err) {
        console.error('Failed to load feature flags:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadFlags();
  }, [isAuthenticated]);

  const isEnabled = useCallback((flagKey) => {
    return flags[flagKey] || false;
  }, [flags]);

  const refreshFlags = useCallback(async () => {
    setIsLoading(true);
    try {
      const loadedFlags = await featureFlagService.getAllFlags(true);
      setFlags(loadedFlags);
      return loadedFlags;
    } catch (err) {
      console.error('Failed to refresh feature flags:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Convenience flag for Part Overview Drawer
  const usePartOverviewDrawer = flags['part_overview_drawer'] || false;

  const value = {
    flags,
    isLoading,
    isEnabled,
    refreshFlags,
    usePartOverviewDrawer,
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export default FeatureFlagContext;
